import {autocomplete} from "@algolia/autocomplete-js";
import {algoliasearch} from "algoliasearch";
import instantsearch from "instantsearch.js";
import aa from "search-insights";
import {
  connectVoiceSearch,
  connectSearchBox,
  connectSortBy,
} from "instantsearch.js/es/connectors";
import {
  hits,
  refinementList,
  currentRefinements,
  infiniteHits,
  clearRefinements,
  configure,
  index,
} from "instantsearch.js/es/widgets";

const ALGOLIA_APP_ID = "MB13IY345Y";
const ALGOLIA_SEARCH_KEY = "d52ccdaa6e5fe8555579ce85c9a0cf1a";
const INSTANT_SEARCH_INDEX_NAME = "shopify_products";
const QUERY_SUGGESTIONS_INDEX_NAME = "shopify_products_query_suggestions";
const ARTICLES_INDEX_NAME = "shopify_articles";

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);

const searchBarClient = {
  ...searchClient,
  search(requests) {
    if (requests.every(({params}) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: "",
          params: "",
        })),
      });
    }

    return searchClient.search(requests);
  },
};

function qs(selector, root = document) {
  return root.querySelector(selector);
}

function qsa(selector, root = document) {
  return Array.from(root.querySelectorAll(selector));
}

function getSearchInput() {
  return qs("#autocomplete .aa-Input") || qs("#autocomplete-0-input");
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getCookie(name) {
  const cookie = {};
  document.cookie.split(";").forEach((el) => {
    const [k, v] = el.split("=");
    cookie[k.trim()] = v;
  });
  return cookie[name];
}

function debounce(fn, time) {
  let timerId;

  return function (...args) {
    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(() => fn(...args), time);
  };
}

let queryID = localStorage.getItem("queryID");
let objectIDS = localStorage.getItem("objectIDS");
let uniqIDS = [];

if ("queryID" in localStorage && "objectIDS" in localStorage && objectIDS) {
  const ids = objectIDS.split(",").slice(0, 20);
  uniqIDS = [...new Set(ids)];
}

function trackConversion() {
  try {
    aa("convertedObjectIDsAfterSearch", {
      appId: ALGOLIA_APP_ID,
      apiKey: ALGOLIA_SEARCH_KEY,
      userToken: getCookie("_ALGOLIA"),
      eventName: "conversion",
      index: INSTANT_SEARCH_INDEX_NAME,
      queryID,
      objectIDs: uniqIDS,
    });
  } catch (error) {}

  try {
    localStorage.removeItem("queryID");
    localStorage.removeItem("objectIDS");
  } catch (error) {}
}

aa("setUserToken", getCookie("_ALGOLIA"));

if ("queryID" in localStorage && "objectIDS" in localStorage) {
  aa("init", {
    appId: ALGOLIA_APP_ID,
    apiKey: ALGOLIA_SEARCH_KEY,
    useCookie: true,
  });
}

const addToCartBtn = qs(`.product-form__buttons button[type='submit']`);
if (addToCartBtn) {
  addToCartBtn.addEventListener("click", () => {
    trackConversion();
  });
}

const search = instantsearch({
  searchClient: searchBarClient,
  indexName: INSTANT_SEARCH_INDEX_NAME,
  insights: true,
  future: {
    preserveSharedStateOnUnmount: true,
  },
});

function getInstantSearchUiState() {
  return {};
}

const searchPageState = getInstantSearchUiState();

let skipInstantSearchUiStateUpdate = false;
let setAutocompleteQuery = null;
let lastVoiceQuery = "";
let objectArray = [];

function setInstantSearchUiState(indexUiState) {
  search.setUiState((uiState) => ({
    ...uiState,
    [INSTANT_SEARCH_INDEX_NAME]: {
      ...uiState[INSTANT_SEARCH_INDEX_NAME],
      page: 1,
      ...indexUiState,
    },
  }));

  const hitsContainer = qs("#searchHitsContainer");
  const resWrapper = qs("#search-res-wrapper");
  const query =
    typeof indexUiState.query === "string" ? indexUiState.query.trim() : "";

  if (query === "") {
    hitsContainer?.classList.add("visually-hidden", "closed");
    resWrapper?.classList.remove("visually-hidden");
  } else {
    hitsContainer?.classList.remove("visually-hidden", "closed");
    resWrapper?.classList.add("visually-hidden");
  }
}

const debouncedSetInstantSearchUiState = debounce(setInstantSearchUiState, 250);
const virtualSearchBox = connectSearchBox(() => {});

function setClass(els, className, fnName) {
  els.forEach((el) => el?.classList?.[fnName]?.(className));
}

function initRefineAccordions() {
  const refineTriggers = qsa(".refine-trigger-inner-search");
  const refinePanels = qsa(".refine");

  refineTriggers.forEach((trigger) => {
    trigger.addEventListener("click", () => {
      const shouldOpen = !trigger.classList.contains("active");
      setClass(refineTriggers, "active", "remove");
      setClass(refinePanels, "show", "remove");

      if (shouldOpen) {
        trigger.classList.add("active");
        trigger.nextElementSibling?.classList.add("show");
      }
    });
  });
}

function initMobileFilters() {
  const selectors = {
    body: document.body,
    mobileMenuToggle: qs("#mobile-menu-toggle-inner-search"),
    menuClose: qs("#menu-close-inncer-search"),
    menuApply: qs("#mobile-apply-inner-search"),
    mobileFilterMenu: qs(".mobile-filter-menu-inner-search"),
    mobileFilterBackdrop: qs(".mobile-filter-backdrop-inner-search"),
    searchHitsContainer: qs("#searchHitsContainer"),
  };

  selectors.mobileMenuToggle?.addEventListener("click", () => {
    selectors.body?.classList.add("blocked");
    selectors.mobileFilterMenu?.classList.add("active");
    selectors.mobileFilterBackdrop?.classList.add("active");
    selectors.searchHitsContainer?.classList.add("blocked");
  });

  const closeMobileFilters = () => {
    selectors.body?.classList.remove("blocked");
    selectors.mobileFilterMenu?.classList.remove("active");
    selectors.mobileFilterBackdrop?.classList.remove("active");
    selectors.searchHitsContainer?.classList.remove("blocked");
  };

  selectors.menuClose?.addEventListener("click", closeMobileFilters);
  selectors.menuApply?.addEventListener("click", closeMobileFilters);
}

function determineProductCard(data, sendEvent) {
  const tagsArray = data.tags || [];
  const collectionsArray = data.collections || [];

  const hasFlavour = () => {
    if (data.named_tags && typeof data.named_tags.Profile === "string") {
      return data.named_tags.Profile;
    }
    return "";
  };

  const isDisposable = () => {
    const all = data.collections ? data.collections.toString() : "";
    return all.includes("disposable-vape-kits");
  };

  const disposableCount = () => {
    let c = "";
    (data.tags || []).forEach((t) => {
      if (t.includes("Profile_")) c = t.replace("Profile_", "");
    });
    return c;
  };

  const isOutOfStockHelper = () => Number(data.variants_inventory_count) <= 0;

  const isOnSale = () => !!data.compare_at_price;

  const areAllVariantsDiscounted = () =>
    data.compare_at_price != 0 &&
    data.variants_min_price == data.variants_max_price;

  const determinePrice = () => {
    const originalPrice = !data.compare_at_price
      ? data.price
      : data.compare_at_price;

    if (Number(data.price) === 0) return "TBC";

    if (isOnSale()) {
      if (areAllVariantsDiscounted()) {
        return `<span class="pre-sale-price"> Was £${originalPrice}</span> <span class="sale-price">Now £${Number(
          data.variants_min_price,
        ).toFixed(2)}</span>`;
      }

      return data.variants_count > 1
        ? `From £${Number(data.variants_min_price).toFixed(2)}`
        : `£${data.price}`;
    }

    return data.variants_count > 1 &&
      data.variants_min_price != data.variants_max_price
      ? `From £${data.variants_min_price}`
      : `£${data.price}`;
  };

  if (data.isPromo) {
    return `
      <div class="card-product card-product--stacked card-carousel-js swiper-slide" data-handle="${data.handle}">
        <a class="card-product__wrapper-link bg-link" href="/products/${data.handle}"></a>
        <div class="card-product__image-wrapper">
          <div class="card-product__imagewrap"></div>
        </div>
        <div class="card-product__info">
          <h4 class="card-product__heading">PROMO CARD</h4>
        </div>
      </div>
    `;
  }

  let topBadgeHTML = "";
  const hasMultiBuy = tagsArray.some(
    (t) => t.includes("Item_pro_Buy") || t.includes("Item_pro1_Buy"),
  );

  if (hasMultiBuy) {
    topBadgeHTML = `<div class="card-product__badge card-product__multiOffer">Multi Buy</div>`;
  } else if (collectionsArray.includes("new")) {
    topBadgeHTML = `<div class="card-product__badge card-product__newHighlight">New in</div>`;
  }

  let offerText = "";
  if (
    tagsArray.some(
      (t) =>
        t.includes("Item_pro_Buy any 4 for £10") ||
        t.includes("Item_pro1_Buy any 4 for £10"),
    )
  ) {
    offerText = "4 for £10";
  } else if (
    tagsArray.some(
      (t) =>
        t.includes("Item_pro_Buy any 5 for £20") ||
        t.includes("Item_pro1_Buy any 5 for £20"),
    )
  ) {
    offerText = "5 for £20";
  } else if (tagsArray.some((t) => t.includes("Item_pro_Buy any 5 for £10"))) {
    offerText = "5 for £10";
  } else if (
    tagsArray.some(
      (t) =>
        t.includes("Item_pro_Buy any 3 for £10") ||
        t.includes("Item_pro1_Buy any 3 for £10"),
    )
  ) {
    offerText = "3 for £10";
  } else if (tagsArray.some((t) => t.includes("Item_pro_Buy any 3 for £20"))) {
    offerText = "3 for £20";
  } else if (
    tagsArray.some(
      (t) =>
        t.includes("Item_pro_Buy any 2 for £25") ||
        t.includes("Item_pro1_Buy any 2 for £25"),
    )
  ) {
    offerText = "2 for £25";
  } else if (tagsArray.some((t) => t.includes("Item_pro_Buy any 2 for £20"))) {
    offerText = "2 for £20";
  } else if (tagsArray.some((t) => t.includes("Item_pro1_Buy any 5 for £25"))) {
    offerText = "5 for £25";
  }

  const offerTextHTML =
    offerText !== ""
      ? `<div class="card-product__offerText">${offerText}</div>`
      : "";

  const flavourText = isDisposable() ? disposableCount() : hasFlavour();
  const flavourHTML =
    flavourText && typeof flavourText === "string"
      ? `<div class="card-product__flavour">${flavourText.replace(/-/g, " | ")}</div>`
      : "";

  let reviewHTML = `
    <div class="tp-stars-rating card-product__rating" data-star-rating="0">
      <div>No reviews yet</div>
    </div>
  `;

  if (data.meta?.ecs?.trustpilot_average_sync) {
    let tpData = data.meta.ecs.trustpilot_average_sync;

    if (typeof tpData === "string") {
      try {
        tpData = JSON.parse(tpData);
      } catch (e) {}
    }

    if (tpData?.average) {
      const average = parseFloat(tpData.average);
      const totalStars = Math.round(average);

      if (totalStars > 0) {
        let stars = "";

        for (let i = 1; i <= 5; i++) {
          if (i <= totalStars) {
            stars += `<svg width="21" height="21" viewBox="0 0 21 21" fill="#F9B754"><path d="M8.36209 8.45391H8.54394L8.59994 8.2809L10.7703 1.57616L12.9407 8.2809L12.9967 8.45391H13.1785H20.2028L14.519 12.5959L14.3725 12.7026L14.4284 12.875L16.5991 19.5735L10.9175 15.4331L10.7703 15.3258L10.6231 15.4331L4.94149 19.5735L7.11225 12.875L7.16811 12.7026L7.02166 12.5959L1.33786 8.45391H8.36209Z" stroke="#F9B754" stroke-width="0.5"/></svg>`;
          } else {
            stars += `<svg width="21" height="21" viewBox="0 0 21 21" fill="none"><path d="M8.36209 8.45391H8.54394L8.59994 8.2809L10.7703 1.57616L12.9407 8.2809L12.9967 8.45391H13.1785H20.2028L14.519 12.5959L14.3725 12.7026L14.4284 12.875L16.5991 19.5735L10.9175 15.4331L10.7703 15.3258L10.6231 15.4331L4.94149 19.5735L7.11225 12.875L7.16811 12.7026L7.02166 12.5959L1.33786 8.45391H8.36209Z" stroke="#F9B754" stroke-width="0.5"/></svg>`;
          }
        }

        reviewHTML = `
          <div class="tp-stars-rating card-product__rating" data-star-rating="${average}">
            ${stars}
          </div>
        `;
      }
    }
  }

  const safeObjectId = JSON.stringify(String(data.objectID ?? ""));

  return `
    <div
      data-pos="${data.__position}"
      data-objectID="${data.objectID}"
      id="Swiper-search-card-${data.id}"
      class="card-product card-product--stacked card-carousel-js swiper-slide search-res-card"
      data-handle="${data.handle}"
      onclick='localStorage.setItem("clickedObjectID", ${safeObjectId})'
    >
      <a class="card-product__wrapper-link bg-link" href="/products/${data.handle}"></a>

      <div class="card-product__image-wrapper">
        ${topBadgeHTML}
        <div class="card-product__imagewrap">
          <img
            class="card-product__thumb ${isOutOfStockHelper() ? "greyed" : ""}"
            src="${data.product_image}&width=400"
            alt="${escapeHtml(data.title)}"
            width="230"
            height="auto"
            loading="lazy"
          />
          ${offerTextHTML}
        </div>
        ${reviewHTML}
      </div>

      <div class="card-product__info">
        <h4 class="card-product__heading">${data.title}</h4>
        ${flavourHTML}
        <div class="card-product__price ${isOutOfStockHelper() ? "greyed" : ""}">
          ${determinePrice() === "£null" ? `£${data.price}` : determinePrice()}
        </div>
      </div>
    </div>
  `;
}

function renderSortByFactory() {
  return (renderOptions, isFirstRender) => {
    const {options, currentRefinement, hasNoResults, refine, widgetParams} =
      renderOptions;

    if (isFirstRender) {
      const select = document.createElement("div");
      select.className = "select-sortby";

      select.addEventListener("click", (event) => {
        const target = event.target.closest(".sort-option");
        if (!target?.id) return;
        refine(target.id);
      });

      widgetParams.container.appendChild(select);
    }

    const select = widgetParams.container.querySelector("div");
    if (!select) return;

    select.disabled = hasNoResults;
    select.innerHTML = `
      ${options
        .map(
          (option) => `
            <span
              class="sort-option"
              id="${option.value}"
              ${option.value === currentRefinement ? "selected" : ""}
            >
              ${option.label}
            </span>
          `,
        )
        .join("")}
    `;
  };
}

const customSortBy = connectSortBy(renderSortByFactory());
const customMobileSortBy = connectSortBy(renderSortByFactory());

function updateNoResultsState(activeQuery) {
  const noResBlock = qs("#searchHits-no-res");

  if (!activeQuery) {
    if (noResBlock) noResBlock.style.display = "none";
    return `<div style="display:none;"></div>`;
  }

  if (window.innerWidth < 1024) {
    const mobileRefine = qs(".mobile-refinement-options.inner-search");
    if (mobileRefine) mobileRefine.style.display = "none";
  } else {
    const desktopRefine = qs("#refine.refine-inner-search");
    if (desktopRefine) desktopRefine.style.display = "none";
  }

  if (noResBlock) noResBlock.style.display = "block";

  return `<div>
    <div class="no-res">
      <div class="search-cat-title">
        <span>No Results Found</span>
        <div class="clear-all" data-padding="195.296875px">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <line x1="0.75" y1="-0.75" x2="20.9543" y2="-0.75" transform="matrix(0.675696 -0.73718 0.675696 0.73718 1.33398 16.7819)" stroke="#231F20" stroke-width="1.5" stroke-linecap="round"></line>
            <line x1="0.75" y1="-0.75" x2="20.9543" y2="-0.75" transform="matrix(0.675696 0.73718 -0.675696 0.73718 0 0.78186)" stroke="#231F20" stroke-width="1.5" stroke-linecap="round"></line>
          </svg>
        </div>
      </div>
      <div>We couldn't find any search results for '${escapeHtml(
        activeQuery,
      )}'. Try a different search.</div>
    </div>
  </div>`;
}

function onHitsItem(data, sendEvent) {
  objectArray.push(data.objectID);
  localStorage.setItem("queryID", data.__queryID);
  localStorage.setItem("objectIDS", objectArray);

  if (window.innerWidth < 1024) {
    const mobileRefine = qs(".mobile-refinement-options.inner-search");
    if (mobileRefine) mobileRefine.style.display = "flex";
  } else {
    const desktopRefine = qs("#refine.refine-inner-search");
    if (desktopRefine) desktopRefine.style.display = "flex";
  }

  const noResBlock = qs("#searchHits-no-res");
  if (noResBlock) noResBlock.style.display = "none";

  return determineProductCard(data, sendEvent);
}

const renderVoiceSearch = (renderOptions) => {
  const {isListening, toggleListening, voiceListeningState} = renderOptions;

  const container = qs("#voicesearch");
  if (!container) return;

  let button = container.querySelector("button");

  if (!button) {
    button = document.createElement("button");
    button.type = "button";
    button.className = "ais-VoiceSearch-button";
    button.setAttribute("aria-label", "Voice search");
    button.addEventListener("click", () => {
      toggleListening();
    });
    container.appendChild(button);
  }

  const prompt = qs("#voice-prompt");
  const autocompleteWrapper = qs("#autocomplete");
  const searchResWrapper = qs("#search-res-wrapper");
  const searchHitsContainer = qs("#searchHitsContainer");

  prompt?.classList.toggle("visually-hidden", !isListening);
  autocompleteWrapper?.classList.toggle("is-listening", isListening);

  button.innerHTML = isListening
    ? "Stop"
    : `
      <span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill="none"></path>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
          <line x1="12" y1="19" x2="12" y2="23"></line>
          <line x1="8" y1="23" x2="16" y2="23"></line>
        </svg>
      </span>
    `;

  const spokenQuery =
    voiceListeningState?.query ||
    voiceListeningState?.value ||
    voiceListeningState?.transcript ||
    "";

  if (
    typeof spokenQuery === "string" &&
    spokenQuery.trim() !== "" &&
    spokenQuery.trim() !== lastVoiceQuery
  ) {
    const cleanQuery = spokenQuery.trim();
    lastVoiceQuery = cleanQuery;

    setInstantSearchUiState({query: cleanQuery});

    if (typeof setAutocompleteQuery === "function") {
      setAutocompleteQuery(cleanQuery);
    }

    searchResWrapper?.classList.add("visually-hidden");
    searchHitsContainer?.classList.remove("visually-hidden", "closed");
  }

  if (!isListening && spokenQuery === "") {
    lastVoiceQuery = "";
  }
};

const customVoiceSearch = connectVoiceSearch(renderVoiceSearch);

search.addWidgets([
  virtualSearchBox({}),
  configure({
    hitsPerPage: 3,
  }),

  index({
    indexName: QUERY_SUGGESTIONS_INDEX_NAME,
  }).addWidgets([
    configure({hitsPerPage: 6}),

    hits({
      container: "#suggestions-hits",
      templates: {
        empty: (_, {html}) => html``,
        item(data, {html, components}) {
          return html`
            <a
              href="/search?q=${encodeURIComponent(data.query)}"
              class="top-product-link top-product-link--button"
            >
              <div>
                ${components.Highlight({hit: data, attribute: "query"})}
              </div>
            </a>
          `;
        },
      },
    }),

    hits({
      container: "#suggestions-hits-duplicated",
      templates: {
        empty: (_, {html}) => html``,
        item(data, {html, components}) {
          return html`
            <a
              href="/search?q=${encodeURIComponent(data.query)}"
              class="top-product-link top-product-link--button"
            >
              <div>
                ${components.Highlight({hit: data, attribute: "query"})}
              </div>
            </a>
          `;
        },
      },
    }),
  ]),

  index({
    indexName: ARTICLES_INDEX_NAME,
  }).addWidgets([
    configure({
      hitsPerPage: 3,
      attributesToHighlight: ["title"],
      highlightPreTag: "<mark>",
      highlightPostTag: "</mark>",
    }),

    hits({
      container: "#blogs-hits",
      templates: {
        empty: (_, {html}) => html``,
        item(data, {html, components}) {
          const blogHandle = data.blog?.handle || "news";

          return html`
            <a
              class="suggestion-link"
              href="/blogs/${blogHandle}/${data.handle}"
            >
              <div class="suggestion-item">
                ${components.Highlight({hit: data, attribute: "title"})}
              </div>
            </a>
          `;
        },
      },
    }),

    hits({
      container: "#blogs-hits-duplicated",
      templates: {
        empty: (_, {html}) => html``,
        item(data, {html, components}) {
          const blogHandle = data.blog?.handle || "news";

          return html`
            <a
              class="suggestion-link"
              href="/blogs/${blogHandle}/${data.handle}"
            >
              <div class="suggestion-item">
                ${components.Highlight({hit: data, attribute: "title"})}
              </div>
            </a>
          `;
        },
      },
    }),
  ]),

  infiniteHits({
    container: "#searchHits",
    templates: {
      empty(results) {
        const activeQuery = results?.query ? results.query.trim() : "";
        return updateNoResultsState(activeQuery);
      },
      item: (data, {sendEvent}) => onHitsItem(data, sendEvent),
    },
    cssClasses: {
      root: "hits-results-list",
      list: "results-grid inside-search",
    },
  }),

  infiniteHits({
    container: "#searchHitsDuplicated",
    templates: {
      empty() {
        const physicalInput = getSearchInput();
        const activeQuery = physicalInput ? physicalInput.value.trim() : "";
        return updateNoResultsState(activeQuery);
      },
      item: (data, {sendEvent}) => onHitsItem(data, sendEvent),
    },
    cssClasses: {
      root: "hits-results-list",
      list: "results-grid inside-search",
    },
  }),

  currentRefinements({
    container: "#current-filters-inner-search",
    transformItems(items) {
      items.forEach((item) => {
        switch (item.label) {
          case "meta.filter.type":
            item.label = "Type";
            break;
          case "meta.filter.flavour_group":
            item.label = "Flavour Group";
            break;
          case "meta.filter.flavour":
            item.label = "Flavour";
            break;
          case "vendor":
            item.label = "Brand";
            break;
          case "meta.filter.size":
            item.label = "Size";
            break;
          case "meta.filter.strength":
            item.label = "Strength";
            break;
          case "meta.filter.ratio":
            item.label = "PG/VG Ratio";
            break;
          case "meta.filter.wattage":
            item.label = "Wattage";
            break;
          case "meta.filter.battery":
            item.label = "Battery";
            break;
          case "meta.filter.resistance":
            item.label = "Resistance";
            break;
          case "meta.filter.offer":
            item.label = "Offers";
            break;
          case "price":
            item.label = "Price";
            break;
          default:
            break;
        }
      });
      return items;
    },
  }),

  clearRefinements({
    container: "#clear-filters-inner-search",
    templates: {
      resetLabel: "Clear All",
    },
  }),

  refinementList({
    container: "#refine-type-inner-search",
    attribute: "meta.filter.type",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
    cssClasses: {root: "group-list"},
  }),
  refinementList({
    container: "#refine-flavourGroup-inner-search",
    attribute: "meta.filter.flavour_group",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
    cssClasses: {root: "group-list"},
  }),
  refinementList({
    container: "#refine-flavour-inner-search",
    attribute: "meta.filter.flavour",
    limit: 10,
    showMore: true,
    showMoreLimit: 100,
    transformItems(items) {
      qs("#refine-flavour-inner-search")?.classList.toggle(
        "opened",
        items.length > 10,
      );
      return items;
    },
    cssClasses: {root: "flavour-list"},
  }),
  refinementList({
    container: "#refine-brand-inner-search",
    attribute: "vendor",
    limit: 10,
    showMore: true,
    showMoreLimit: 100,
    transformItems(items) {
      qs("#refine-brand-inner-search")?.classList.toggle(
        "opened",
        items.length > 10,
      );
      return items;
    },
  }),
  refinementList({
    container: "#refine-size-inner-search",
    attribute: "meta.filter.size",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-nic-inner-search",
    attribute: "meta.filter.strength",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-ratio-inner-search",
    attribute: "meta.filter.ratio",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-wattage-inner-search",
    attribute: "meta.filter.wattage",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-battery-inner-search",
    attribute: "meta.filter.battery",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-resistance-inner-search",
    attribute: "meta.filter.resistance",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#refine-offers-inner-search",
    attribute: "meta.filter.offer",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),

  customSortBy({
    container: qs("#sort-by-inner-search"),
    items: [
      {label: "Featured", value: "shopify_products"},
      {
        label: "Best Selling",
        value: "shopify_products_recently_ordered_count_desc",
      },
      {label: "Price ascending", value: "shopify_products_price_asc"},
      {label: "Price descending", value: "shopify_products_price_desc"},
      {label: "Title ascending", value: "shopify_products_title_asc"},
      {label: "Title descending", value: "shopify_products_title_desc"},
      {label: "Created ascending", value: "shopify_products_published_at_asc"},
      {
        label: "Created descending",
        value: "shopify_products_published_at_desc",
      },
    ],
  }),

  clearRefinements({
    container: "#mobile-clear-inner-search",
    templates: {
      resetLabel: "Clear",
    },
  }),

  refinementList({
    container: "#mobile-refine-type-inner-search",
    attribute: "meta.filter.type",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
    cssClasses: {root: "group-list"},
  }),
  refinementList({
    container: "#mobile-refine-flavourGroup-inner-search",
    attribute: "meta.filter.flavour_group",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
    cssClasses: {root: "group-list"},
  }),
  refinementList({
    container: "#mobile-refine-flavour-inner-search",
    attribute: "meta.filter.flavour",
    limit: 10,
    showMore: true,
    showMoreLimit: 100,
    cssClasses: {root: "flavour-list"},
  }),
  refinementList({
    container: "#mobile-refine-brand-inner-search",
    attribute: "vendor",
    limit: 10,
    showMore: true,
    showMoreLimit: 100,
  }),
  refinementList({
    container: "#mobile-refine-size-inner-search",
    attribute: "meta.filter.size",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-nic-inner-search",
    attribute: "meta.filter.strength",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-ratio-inner-search",
    attribute: "meta.filter.ratio",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-wattage-inner-search",
    attribute: "meta.filter.wattage",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-battery-inner-search",
    attribute: "meta.filter.battery",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-resistance-inner-search",
    attribute: "meta.filter.resistance",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),
  refinementList({
    container: "#mobile-refine-offers-inner-search",
    attribute: "meta.filter.offer",
    limit: 10,
    showMore: true,
    showMoreLimit: 40,
  }),

  customMobileSortBy({
    container: qs("#mobile-sort-by-inner-search"),
    items: [
      {label: "Featured", value: "shopify_products"},
      {
        label: "Best Selling",
        value: "shopify_products_recently_ordered_count_desc",
      },
      {label: "Price ascending", value: "shopify_products_price_asc"},
      {label: "Price descending", value: "shopify_products_price_desc"},
      {label: "Title ascending", value: "shopify_products_title_asc"},
      {label: "Title descending", value: "shopify_products_title_desc"},
      {label: "Created ascending", value: "shopify_products_published_at_asc"},
      {
        label: "Created descending",
        value: "shopify_products_published_at_desc",
      },
    ],
  }),

  customVoiceSearch({
    container: qs("#voicesearch"),
    language: "en-GB",
    searchAsYouSpeak: true,
    additionalQueryParameters() {
      return {
        ignorePlurals: false,
      };
    },
  }),
]);

search.on("render", () => {
  const searchInput = getSearchInput();
  const currentQuery = searchInput ? searchInput.value.trim() : "";

  const staticBlock = qs("#searchHitsStatic");
  const dynamicBlock = qs("#searchHitsDynamic");
  const dynamicBlockDuplicated = qs("#searchHitsDynamicDuplicated");

  const suggestionsContainer = qs("#suggestions-hits .ais-Hits");
  const blogsContainer = qs("#blogs-hits .ais-Hits");

  const suggestionsWrapper = qs("#suggestions-hits")?.parentElement;
  const blogsWrapper = qs("#blogs-hits")?.parentElement;
  const suggestionsWrapperDuplicated = qs(
    "#suggestions-hits-duplicated",
  )?.parentElement;
  const blogsWrapperDuplicated = qs("#blogs-hits-duplicated")?.parentElement;

  const isSuggestionsEmpty = suggestionsContainer
    ? suggestionsContainer.classList.contains("ais-Hits--empty")
    : true;
  const isBlogsEmpty = blogsContainer
    ? blogsContainer.classList.contains("ais-Hits--empty")
    : true;

  if (currentQuery !== "") {
    if (suggestionsWrapper) {
      suggestionsWrapper.style.display = isSuggestionsEmpty ? "none" : "block";
    }
    if (suggestionsWrapperDuplicated) {
      suggestionsWrapperDuplicated.style.display = isSuggestionsEmpty
        ? "none"
        : "block";
    }

    if (blogsWrapper) {
      blogsWrapper.style.display = isBlogsEmpty ? "none" : "block";
    }
    if (blogsWrapperDuplicated) {
      blogsWrapperDuplicated.style.display = isBlogsEmpty ? "none" : "block";
    }

    if (isSuggestionsEmpty && isBlogsEmpty) {
      staticBlock?.classList.remove("visually-hidden");
      dynamicBlock?.classList.add("visually-hidden");
      dynamicBlockDuplicated?.classList.add("visually-hidden");
    } else {
      staticBlock?.classList.add("visually-hidden");
      dynamicBlock?.classList.remove("visually-hidden");
      dynamicBlockDuplicated?.classList.remove("visually-hidden");
    }
  } else {
    staticBlock?.classList.remove("visually-hidden");
    dynamicBlock?.classList.add("visually-hidden");
    dynamicBlockDuplicated?.classList.add("visually-hidden");
  }

  qsa(".ais-RefinementList").forEach((facet) => {
    const facetCount = facet.querySelector(".ais-RefinementList-list");
    const shouldBlock =
      facet.childElementCount === 0 ||
      (facetCount != null && facetCount.childElementCount === 1);

    const parent = facet.parentNode?.previousElementSibling;
    parent?.classList.toggle("blocked", shouldBlock);
    parent?.parentNode?.classList?.toggle("blocked", shouldBlock);
  });
});

search.start();

const autocompleteContainer = qs("#autocomplete");
let isAlreadyMounted = false;

if (autocompleteContainer) {
  const existingAutocompletes =
    autocompleteContainer.querySelectorAll(".aa-Autocomplete");
  existingAutocompletes.forEach((el) => {
    if (el.id !== "dummy-aa-wrapper") {
      isAlreadyMounted = true;
    }
  });
}

if (autocompleteContainer && !isAlreadyMounted) {
  const {setQuery} = autocomplete({
    container: "#autocomplete",
    placeholder: "Search products, brands or category",
    detachedMediaQuery: "none",
    insights: false,
    initialState: {
      query: searchPageState.query || "",
    },

    // onSubmit({state}) {
    //   setInstantSearchUiState({query: state.query});
    // },

    onSubmit({state}) {
      const query = (state.query || "").trim();

      if (!query) return;

      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    },

    onReset() {
      setInstantSearchUiState({query: ""});
      qs("#searchHitsContainer")?.classList.add("visually-hidden", "closed");
      qs("#search-res-wrapper")?.classList.remove("visually-hidden");
    },

    onFocus() {
      qs("#search-res-wrapper")?.classList.remove("visually-hidden");
    },

    onStateChange({prevState, state}) {
      const clearButton = qs(".aa-ClearButton");
      if (clearButton) {
        clearButton.innerHTML = "Clear";
      }

      const hitsContainer = qs("#searchHitsContainer");
      if (hitsContainer) {
        hitsContainer.style.overflow = "auto";
      }

      const staticLeft = qs("#searchHitsStatic");
      const dynamicLeft = qs("#searchHitsDynamic");
      const dynamicLeftDuplicated = qs("#searchHitsDynamicDuplicated");

      const viewAllContainer = qs("#search-bar-view-all");
      if (viewAllContainer) {
        viewAllContainer.innerHTML = state.query
          ? `
            <a href="/search?q=${encodeURIComponent(state.query)}" class="search-query-link search-query-link--button">
              <p class="search-query-text">View all "<span>${escapeHtml(state.query)}</span>"</p>
            </a>
          `
          : ``;

        const viewAllBtn = viewAllContainer.querySelector(
          ".search-query-link--button",
        );
        if (viewAllBtn) {
          viewAllBtn.addEventListener("click", () => {
            setInstantSearchUiState({query: state.query});
          });
        }
      }

      const searchStatsContainer = qs("#search-stats");
      if (searchStatsContainer) {
        searchStatsContainer.innerHTML = state.query
          ? `
            <div class="search-cat-title">
              <div class="search-cat-title__inner">
                <span>Products</span>
               <a href="/search?q=${encodeURIComponent(state.query)}" class="search-query-link search-query-link--button">
              <p class="search-query-text">View all "${escapeHtml(state.query)}"</p>
            </a>
              </div>

              <div class="clear-all hide-mobile" data-padding="195.296875px">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                  <line x1="0.75" y1="-0.75" x2="20.9543" y2="-0.75" transform="matrix(0.675696 -0.73718 0.675696 0.73718 1.33398 16.7819)" stroke="#231F20" stroke-width="1.5" stroke-linecap="round"></line>
                  <line x1="0.75" y1="-0.75" x2="20.9543" y2="-0.75" transform="matrix(0.675696 0.73718 -0.675696 0.73718 0 0.78186)" stroke="#231F20" stroke-width="1.5" stroke-linecap="round"></line>
                </svg>
              </div>
            </div>
          `
          : ``;

        const statsBtn = searchStatsContainer.querySelector(
          ".search-query-link--button",
        );
        if (statsBtn) {
          statsBtn.addEventListener("click", () => {
            setInstantSearchUiState({query: state.query});
          });
        }
      }

      if (state?.query === "") {
        qs("#searchHitsContainer")?.classList.add("visually-hidden", "closed");
        qs("#search-res-wrapper")?.classList.remove("visually-hidden");

        staticLeft?.classList.remove("visually-hidden");
        dynamicLeft?.classList.add("visually-hidden");
        dynamicLeftDuplicated?.classList.add("visually-hidden");
      } else {
        qs("#search-res-wrapper")?.classList.add("visually-hidden");
        qs("#searchHitsContainer")?.classList.remove(
          "visually-hidden",
          "closed",
        );
      }

      if (!skipInstantSearchUiStateUpdate && prevState.query !== state.query) {
        debouncedSetInstantSearchUiState({query: state.query});
      }

      skipInstantSearchUiStateUpdate = false;
    },
  });

  setAutocompleteQuery = setQuery;
  window.algoliaSetQuery = setQuery;
}

getSearchInput()?.addEventListener("focus", () => {
  qs("#search-res-wrapper")?.classList.remove("visually-hidden");
  qs("#searchHitsContainer")?.classList.add("visually-hidden", "closed");
  document.body.classList.add("stop-scrolling");
});

document.addEventListener("click", (event) => {
  const closeTarget = event.target.closest(
    ".search-bar-overlay, .clear-all, #clear-all",
  );
  if (!closeTarget) return;

  qs("#autocomplete")?.classList.remove("is-focused");

  const headerWrapperTop = qs("#h-wrapper-top");
  if (headerWrapperTop) {
    headerWrapperTop.style.paddingTop = "";
    headerWrapperTop.style.marginTop = "";
  }

  window.dispatchEvent(new Event("resize"));

  setInstantSearchUiState({query: ""});

  qsa(".aa-Form").forEach((form) => form.reset());
  document.body.classList.remove("stop-scrolling");
  getSearchInput()?.blur();
  qs("#search-res-wrapper")?.classList.add("visually-hidden");
  qs("#searchHitsContainer")?.classList.add("visually-hidden", "closed");

  window.scrollTo(0, 0);
});

const menuDrawer = qs("#Details-menu-drawer-container");
if (menuDrawer) {
  menuDrawer.addEventListener("toggle", (event) => {
    if (!event.target.open) return;

    const clearAllElement = qs(".clear-all");
    if (clearAllElement) {
      clearAllElement.dispatchEvent(new Event("click", {bubbles: true}));
    } else {
      qsa(".aa-Form").forEach((form) => form.reset());
      getSearchInput()?.blur();
      qs("#search-res-wrapper")?.classList.add("visually-hidden");
      qs("#searchHitsContainer")?.classList.add("visually-hidden", "closed");
    }
  });
}

function setVisualViewportHeight() {
  if (!window.visualViewport) return;
  document.documentElement.style.setProperty(
    "--vv-height",
    `${window.visualViewport.height}px`,
  );
}

if (window.visualViewport) {
  window.visualViewport.addEventListener("resize", setVisualViewportHeight);
  setVisualViewportHeight();
}

document.addEventListener("DOMContentLoaded", () => {
  initRefineAccordions();
  initMobileFilters();

  const enforceSearchVisibility = () => {
    const searchInput = getSearchInput();
    if (!searchInput) return;

    const hasText = searchInput.value.trim() !== "";
    const trendingWrapper = qs("#search-res-wrapper");
    const hitsContainer = qs("#searchHitsContainer");

    if (hasText) {
      if (
        trendingWrapper &&
        !trendingWrapper.classList.contains("visually-hidden")
      ) {
        trendingWrapper.classList.add("visually-hidden");
      }
      hitsContainer?.classList.remove("visually-hidden", "closed");
    }
  };

  ["focusin", "mousedown", "mouseup", "click", "input", "keyup"].forEach(
    (eventType) => {
      document.addEventListener(eventType, (e) => {
        const target = e.target;
        const isInput =
          target?.id === "autocomplete-0-input" ||
          target?.classList?.contains("aa-Input");
        const isClearBtn = target?.closest?.(".aa-ClearButton");

        if (isClearBtn) {
          const forceClearUI = () => {
            qs("#search-res-wrapper")?.classList.remove("visually-hidden");
            qs("#searchHitsContainer")?.classList.add(
              "visually-hidden",
              "closed",
            );
          };

          forceClearUI();
          setTimeout(forceClearUI, 10);
          setTimeout(forceClearUI, 50);
          setTimeout(forceClearUI, 150);
        } else if (isInput) {
          enforceSearchVisibility();
          setTimeout(enforceSearchVisibility, 10);
          setTimeout(enforceSearchVisibility, 50);
          setTimeout(enforceSearchVisibility, 150);
        }
      });
    },
  );

  const trendingWrapperTarget = qs("#search-res-wrapper");
  if (trendingWrapperTarget) {
    const observer = new MutationObserver(() => {
      const searchInput = getSearchInput();

      if (searchInput && searchInput.value.trim() !== "") {
        if (!trendingWrapperTarget.classList.contains("visually-hidden")) {
          trendingWrapperTarget.classList.add("visually-hidden");
          qs("#searchHitsContainer")?.classList.remove(
            "visually-hidden",
            "closed",
          );
        }
      }
    });

    observer.observe(trendingWrapperTarget, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }
});
